const TAB_IDS = ['overview', 'information', 'addresses', 'orders', 'wishlist'];

const PATH_TAB_MAP = {
  '/pages/account-overview': 'overview',
  '/pages/account-information': 'information',
  '/pages/account-addresses': 'addresses',
  '/pages/account-orders': 'orders',
  '/pages/account-wishlist': 'wishlist',
};

const CANONICAL_PATH = '/pages/account-overview';

class FluevogAccountTabs extends HTMLElement {
  #abort = null;

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
    this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));

    if (!this.tabs.length || !this.panels.length) return;

    const initialTab = this.#resolveInitialTab();
    this.selectTab(initialTab, { updateHistory: false });
    this.#normalizeUrl(initialTab);

    for (const tab of this.tabs) {
      tab.addEventListener('click', () => {
        const tabId = tab.dataset.tab;
        if (tabId) this.selectTab(tabId);
      }, { signal });
    }

    this.addEventListener('keydown', (event) => this.#handleKeydown(event), { signal });

    this.querySelectorAll('.fv-account__tab-trigger').forEach((trigger) => {
      trigger.addEventListener('click', () => {
        const tabId = trigger.dataset.tab;
        if (tabId) this.selectTab(tabId);
      }, { signal });
    });

    window.addEventListener('hashchange', () => {
      const tabId = this.#tabFromHash();
      if (tabId) this.selectTab(tabId, { updateHistory: false });
    }, { signal });
  }

  disconnectedCallback() {
    this.#abort?.abort();
  }

  selectTab(tabId, { updateHistory = true, focusTab = false } = {}) {
    if (!TAB_IDS.includes(tabId)) return;

    const tabIndex = this.tabs.findIndex((tab) => tab.dataset.tab === tabId);
    if (tabIndex === -1) return;

    for (const [index, tab] of this.tabs.entries()) {
      const isSelected = index === tabIndex;
      tab.setAttribute('aria-selected', isSelected ? 'true' : 'false');
      tab.tabIndex = isSelected ? 0 : -1;
      tab.classList.toggle('is-active', isSelected);
    }

    for (const panel of this.panels) {
      const isActive = panel.dataset.tab === tabId;
      panel.hidden = !isActive;
    }

    this.#updateBreadcrumb(this.tabs[tabIndex].textContent?.trim() || '');

    if (updateHistory) {
      this.#normalizeUrl(tabId);
    }

    if (focusTab) {
      this.tabs[tabIndex].focus();
    }
  }

  #resolveInitialTab() {
    return this.#tabFromHash()
      || this.#tabFromPath()
      || this.dataset.initialTab
      || 'overview';
  }

  #tabFromHash() {
    const hash = window.location.hash.replace(/^#/, '');
    return TAB_IDS.includes(hash) ? hash : null;
  }

  #tabFromPath() {
    const path = window.location.pathname.replace(/\/$/, '');
    return PATH_TAB_MAP[path] || null;
  }

  #normalizeUrl(tabId) {
    const hash = tabId === 'overview' ? '' : `#${tabId}`;
    const nextUrl = `${CANONICAL_PATH}${hash}`;
    const currentUrl = `${window.location.pathname}${window.location.hash}`;

    if (currentUrl !== nextUrl) {
      window.history.replaceState(null, '', nextUrl);
    }
  }

  #updateBreadcrumb(label) {
    const breadcrumb = document.querySelector('[data-account-breadcrumb-current]');
    if (breadcrumb && label) {
      breadcrumb.textContent = label;
    }
  }

  #handleKeydown(event) {
    const target = event.target;
    if (!(target instanceof HTMLElement) || target.getAttribute('role') !== 'tab') return;

    const currentIndex = this.tabs.indexOf(target);
    if (currentIndex === -1) return;

    const keyActions = {
      ArrowDown: 1,
      ArrowUp: -1,
      Home: -currentIndex,
      End: this.tabs.length - 1 - currentIndex,
    };

    const offset = keyActions[event.key];
    if (offset === undefined) return;

    event.preventDefault();

    const nextIndex = (currentIndex + offset + this.tabs.length) % this.tabs.length;
    const nextTab = this.tabs[nextIndex];
    const tabId = nextTab.dataset.tab;

    if (tabId) {
      this.selectTab(tabId, { focusTab: true });
    }
  }
}

if (!customElements.get('fluevog-account-tabs')) {
  customElements.define('fluevog-account-tabs', FluevogAccountTabs);
}
